import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  PlusCircle, RefreshCw, Layers3, Search, Pencil, Trash2, Power, X, Filter,
} from "lucide-react";

const ENGINE_LABEL = { QD: "Quantity Discount", GROWTH: "Growth", MILESTONE: "Milestone" };
const EDIT_ROLES = ["Super Administrator", "Corporate Scheme Admin", "OpCo Scheme Manager", "RSO Scheme Creator"];
const ADMIN_ROLES = ["Super Administrator", "Corporate Scheme Admin", "OpCo Scheme Manager"];

export default function Schemes() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = EDIT_ROLES.includes(user?.role);
  const canAdmin = ADMIN_ROLES.includes(user?.role);

  const [search, setSearch] = useState("");
  const [opco, setOpco] = useState("all");
  const [engine, setEngine] = useState("all");
  const [status, setStatus] = useState("all");
  const [toDelete, setToDelete] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["schemes"],
    queryFn: async () => (await api.get("/schemes")).data,
  });

  const recalc = useMutation({
    mutationFn: async (id) => (await api.post("/calc/recalculate", { scheme_id: id })).data,
    onSuccess: (d) => { toast.success(`Recalculated ${d.updated} dealers (run ${d.run_id.slice(0, 8)})`); qc.invalidateQueries(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Recalculation failed"),
  });
  const setStatusMut = useMutation({
    mutationFn: async ({ id, s }) => (await api.patch(`/schemes/${id}/status`, { status: s })).data,
    onSuccess: (r) => { toast.success(`Scheme ${r.status}`); qc.invalidateQueries({ queryKey: ["schemes"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Status change failed"),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/schemes/${id}`)).data,
    onSuccess: () => { toast.success("Scheme deleted"); setToDelete(null); qc.invalidateQueries({ queryKey: ["schemes"] }); },
    onError: (e) => { toast.error(e.response?.data?.detail || "Delete failed"); setToDelete(null); },
  });

  const opcoOptions = useMemo(() => Array.from(new Set((data?.schemes || []).map((s) => s.opco_code).filter(Boolean))), [data]);

  const filtered = useMemo(() => {
    let list = data?.schemes || [];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((s) => s.name.toLowerCase().includes(q) || (s.code || "").toLowerCase().includes(q));
    }
    if (opco !== "all") list = list.filter((s) => s.opco_code === opco);
    if (engine !== "all") list = list.filter((s) => s.engine === engine);
    if (status !== "all") list = list.filter((s) => s.status === status);
    return list;
  }, [data, search, opco, engine, status]);

  const hasFilters = search || opco !== "all" || engine !== "all" || status !== "all";
  const clearFilters = () => { setSearch(""); setOpco("all"); setEngine("all"); setStatus("all"); };

  if (isLoading || !data) return <Loading label="Loading schemes…" />;

  const selCls = "h-9 rounded-md border border-slate-200 px-2.5 bg-white text-sm text-slate-700";

  return (
    <div>
      <PageHeader title="Schemes" subtitle="All configured incentive schemes across ACC & ACL"
        actions={
          canEdit && (
            <Button data-testid="new-scheme-btn" onClick={() => nav("/schemes/new")}
              className="gap-2 bg-[#0F2C59] hover:bg-[#00529B]">
              <PlusCircle size={16} /> New Scheme
            </Button>
          )
        } />

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 mb-5 flex flex-col lg:flex-row lg:items-center gap-3" data-testid="scheme-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="scheme-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or code…" className="pl-9 h-9" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={15} className="text-slate-400" />
          <select data-testid="filter-opco" className={selCls} value={opco} onChange={(e) => setOpco(e.target.value)}>
            <option value="all">All OpCos</option>
            {opcoOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select data-testid="filter-engine" className={selCls} value={engine} onChange={(e) => setEngine(e.target.value)}>
            <option value="all">All Engines</option>
            {Object.entries(ENGINE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select data-testid="filter-status" className={selCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          {hasFilters && (
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500" data-testid="clear-filters-btn" onClick={clearFilters}>
              <X size={14} /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="text-xs text-slate-400 mb-3" data-testid="scheme-count">{filtered.length} of {data.schemes.length} schemes</div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-400">No schemes match your filters.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <div key={s.id} data-testid={`scheme-card-${s.code}`}
              className="kpi-card bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg adani-navy-gradient flex items-center justify-center text-white">
                  <Layers3 size={18} />
                </div>
                <Pill tone={s.status}>{s.status}</Pill>
              </div>
              <div className="font-heading font-semibold text-slate-900">{s.name}</div>
              <div className="text-xs text-slate-400 font-mono mt-0.5">{s.code} · v{s.version}</div>
              <div className="flex items-center gap-2 mt-3 text-xs text-slate-500 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-slate-100">{s.opco_code}</span>
                <span className="px-2 py-0.5 rounded bg-blue-50 text-[#00529B]">{ENGINE_LABEL[s.engine] || s.engine}</span>
                <span>{s.period}</span>
              </div>

              <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                {canEdit && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" variant="outline" data-testid={`edit-btn-${s.code}`}
                      onClick={() => nav(`/schemes/${s.id}/edit`)} className="gap-1.5">
                      <Pencil size={14} /> Edit
                    </Button>
                    {canAdmin && (
                      s.status === "active" ? (
                        <Button size="sm" variant="outline" data-testid={`deactivate-btn-${s.code}`}
                          onClick={() => setStatusMut.mutate({ id: s.id, s: "draft" })} className="gap-1.5">
                          <Power size={14} /> Deactivate
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" data-testid={`activate-btn-${s.code}`}
                          onClick={() => setStatusMut.mutate({ id: s.id, s: "active" })} className="gap-1.5 text-emerald-700 border-emerald-200">
                          <Power size={14} /> Activate
                        </Button>
                      )
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" data-testid={`recalc-btn-${s.code}`}
                    disabled={recalc.isPending} onClick={() => recalc.mutate(s.id)} className="gap-1.5">
                    <RefreshCw size={14} className={recalc.isPending ? "animate-spin" : ""} /> Recalc
                  </Button>
                  {canAdmin && (
                    <Button size="sm" variant="outline" data-testid={`delete-btn-${s.code}`}
                      disabled={s.status === "active"} onClick={() => setToDelete(s)}
                      className="gap-1.5 text-red-600 border-red-200 disabled:opacity-40"
                      title={s.status === "active" ? "Deactivate before deleting" : "Delete scheme"}>
                      <Trash2 size={14} /> Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white" data-testid="delete-scheme-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the scheme and its calculation ledger entries. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" className="bg-red-600 hover:bg-red-700"
              onClick={() => del.mutate(toDelete.id)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
